/**
 * Trick play on a remote control: the accelerating key-repeat model (TV-5).
 *
 * ## 🚨 Ten-foot means there is NO POINTER. This is not a slider.
 *
 * A scrub bar on a phone is a direct-manipulation control: the thumb goes where
 * the finger is, and granularity is a pixel question. A hotel remote has four
 * arrows and an OK button. The only input is **how many times the key has
 * fired**, so seek granularity is entirely a key-repeat design problem, and
 * getting it wrong produces one of two familiar bad televisions:
 *
 * - a **fixed 10-second step**, where crossing a two-hour film means holding
 *   right for a minute and watching a bar crawl; or
 * - a **fixed large step**, where a guest who wanted to skip the recap can
 *   never land on the scene they wanted and gives up.
 *
 * ## WHAT IT FEELS LIKE
 *
 * A tap is ten seconds — always, no exceptions, because a deliberate single
 * press is a guest saying "I missed that line". Hold the key and the film
 * starts moving in half-minutes after about a second, in minutes after about
 * two and a half, and in two-minute strides after four. End to end on a
 * two-hour film is roughly **twelve seconds of holding**, and letting go for
 * half a second puts you back at ten-second taps — so overshooting is always
 * one release and one tap away from being fixed, never a second long hold in
 * the other direction.
 *
 * The ladder is stepped rather than continuous on purpose. A smooth exponential
 * feels excellent under a thumb watching a live preview and awful on a panel
 * whose thumbnail arrives 200 ms late: the guest cannot predict where the next
 * press lands. Four fixed gears are predictable, and predictable is what a
 * ten-foot control is for.
 *
 * ## 🚨 And the seek itself is COMMITTED, not issued per press
 *
 * Every `video.currentTime = x` on this hardware is a decoder flush and refill.
 * A guest holding right for four seconds at a typical 6 Hz repeat would issue
 * **twenty-four** of them, which on a pooled hardware decoder is how a panel
 * stops playing anything at all ([[panel-video-encoding]]). So the ladder moves
 * a *target*, the surface renders the target, and exactly one seek is issued
 * when the keys go quiet ({@link SCRUB_COMMIT_MS}) or the guest presses OK.
 * That is also why the ladder is pure: the commit policy is testable without a
 * player, and the player never sees the intermediate positions at all.
 */

/** One deliberate tap. The number the ticket asks for, and the floor. */
export const SEEK_STEP_SECONDS = 10;

/**
 * Longer than this between two presses and the ladder resets to a tap.
 *
 * 500 ms is above every TV remote's auto-repeat interval (typically 100-200 ms
 * once repeat starts) and below any plausible deliberate double-tap rhythm, so
 * a held key never resets and two separate presses never accelerate.
 */
export const REPEAT_GAP_MS = 500;

/** Quiet for this long and the accumulated target is committed as one seek. */
export const SCRUB_COMMIT_MS = 350;

/**
 * The gears. `after` is the number of presses already made in this run.
 *
 * Read as: presses 1-3 move 10 s, 4-8 move 30 s, 9-15 move 60 s, and everything
 * after that moves 120 s. Ordered high to low so lookup is a `find`.
 */
export const SEEK_LADDER: readonly { after: number; step: number }[] = [
  { after: 15, step: 120 },
  { after: 8, step: 60 },
  { after: 3, step: 30 },
  { after: 0, step: SEEK_STEP_SECONDS },
];

/** How far the Nth press of a run moves the target. `presses` is 0-based. */
export function seekStepSeconds(presses: number): number {
  const gear = SEEK_LADDER.find((g) => presses >= g.after);
  return gear ? gear.step : SEEK_STEP_SECONDS;
}

export interface ScrubState {
  /** Where the guest is aiming, in seconds. */
  target: number;
  /** Presses in the current run. Resets after {@link REPEAT_GAP_MS}. */
  presses: number;
  /** When the last press arrived. */
  lastPressAt: number;
  /** True while a target is pending and unseeked. */
  scrubbing: boolean;
}

export interface SeekBounds {
  /** Earliest seekable second. 0 for VOD; the DVR edge for live. */
  start: number;
  /** Latest seekable second. The duration for VOD; the live edge for live. */
  end: number;
}

/** A run that has not started. */
export function idleScrub(): ScrubState {
  return { target: 0, presses: 0, lastPressAt: 0, scrubbing: false };
}

/**
 * Clamp a target into the seekable window.
 *
 * The end is pulled back by a second: landing exactly on `duration` fires
 * `ended` and takes the guest out of the film they were trying to navigate,
 * which is the single most annoying way for a seek bar to be technically
 * correct.
 */
export function clampToBounds(seconds: number, bounds: SeekBounds): number {
  const end = Math.max(bounds.start, bounds.end - 1);
  if (!Number.isFinite(seconds)) return bounds.start;
  return Math.min(Math.max(seconds, bounds.start), end);
}

export interface ScrubPress {
  /** -1 for rewind, +1 for fast forward. */
  direction: -1 | 1;
  /** `Date.now()` at the press. Injected so the ladder is testable. */
  now: number;
  /** The playhead, used only when a run STARTS. */
  currentTime: number;
  bounds: SeekBounds;
}

/**
 * Apply one arrow press to a scrub run.
 *
 * A run starts from the playhead and continues from its own target, which is
 * what makes a hold accumulate. The gap check is on the *previous* press, so a
 * key released and pressed again half a second later starts a fresh run at ten
 * seconds even though the target is carried over — the guest keeps their place
 * and loses only the acceleration, which is exactly the correction they were
 * making.
 */
export function pressScrub(state: ScrubState, press: ScrubPress): ScrubState {
  // 🚨 An empty window is a REFUSAL, not a seek to zero.
  //
  // `bounds` is `{start: 0, end: 0}` for the whole gap between `load()`
  // resolving and `loadedmetadata`, and for any player build that does not
  // expose a seek range. Clamping into an empty window yields 0, so without
  // this an arrow press in that window would commit a seek to the start and
  // restart the film the guest was two minutes into. The same STATE object is
  // returned, so a caller can tell nothing happened by identity.
  if (!(press.bounds.end > press.bounds.start)) return state;
  const continuing =
    state.scrubbing && press.now - state.lastPressAt <= REPEAT_GAP_MS;
  const presses = continuing ? state.presses : 0;
  const from = state.scrubbing ? state.target : press.currentTime;
  const step = seekStepSeconds(presses) * press.direction;
  return {
    target: clampToBounds(from + step, press.bounds),
    presses: presses + 1,
    lastPressAt: press.now,
    scrubbing: true,
  };
}

/** Should the pending target be committed yet? */
export function shouldCommit(state: ScrubState, now: number): boolean {
  return state.scrubbing && now - state.lastPressAt >= SCRUB_COMMIT_MS;
}

/** How far the pending target is from the playhead, as `+1:30` / `-0:20`. */
export function formatDelta(seconds: number): string {
  const sign = seconds < 0 ? "-" : "+";
  return `${sign}${formatClock(Math.abs(seconds))}`;
}

/** `1:04:09` over an hour, `4:09` under it. Never `01:04:09`. */
export function formatClock(seconds: number): string {
  const total = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}
